import type {
  PlaybookId,
  PlaybookView,
} from "@repo/app-types/entities/playbook";
import type { PlaybookViewDefaults } from "@repo/app-types/entities/playbookViewDefaults";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { DisplayColumnsMenuTrigger } from "@repo/ui/Menu/displayColumnsMenuTrigger";
import { MultiSelectMenuPopover } from "@repo/ui/Menu/multiSelectMenuPopover";
import { SimpleMenuFooter } from "@repo/ui/Menu/simpleMenuFooter";
import type { MultiSelectSection } from "@repo/ui/Select/multiSelect";
import { useCallback, useMemo, useState, type FC } from "react";
import { MenuTrigger } from "react-aria-components";
import { useTeammateAuthorization } from "../../../helpers/teammateAuthorizationContext.js";
import {
  SetPlaybookViewDefaultAction,
  SetPlaybookViewDefaultConfirmation,
} from "../../filters/setPlaybookViewDefaultAction.js";
import { buildPlaybookViewColumnDefaultsPatch } from "./playbookViewColumnDefaults.js";

interface DisplayColumnsSelectorProps {
  playbookId?: PlaybookId;
  view?: PlaybookView;
  /** Cached shared defaults for this view; absent fields use built-in defaults. */
  viewDefaults?: PlaybookViewDefaults;
  /**
   * Optional "Performance metrics" sections. When provided, rendered
   * first in the menu (above section / custom-field groups).
   */
  metricColumnSections?: readonly MultiSelectSection[];
  metricColumnSelectedIds?: readonly string[];
  /** Includes preserved defaults for metric columns unavailable on this surface. */
  metricColumnDefaultSelectedIds?: readonly string[];
  onMetricColumnSelectionChange?: (keys: readonly string[]) => void;
  sectionColumnSections?: readonly MultiSelectSection[];
  sectionColumnSelectedIds?: readonly string[];
  onSectionColumnSelectionChange?: (keys: readonly string[]) => void;
  customFieldSections: readonly MultiSelectSection[];
  customFieldSelectedIds: readonly string[];
  onCustomFieldSelectionChange: (keys: readonly string[]) => void;
}

/**
 * A combined "Display columns" dropdown that merges the optional
 * "Performance metrics" group with playbook section column options and
 * custom field column options into a single multi-select menu.
 */
export const DisplayColumnsSelector: FC<DisplayColumnsSelectorProps> = (
  props,
) => {
  const {
    playbookId,
    view,
    viewDefaults,
    metricColumnSections = [],
    metricColumnSelectedIds = [],
    metricColumnDefaultSelectedIds = metricColumnSelectedIds,
    onMetricColumnSelectionChange,
    sectionColumnSections = [],
    sectionColumnSelectedIds = [],
    onSectionColumnSelectionChange,
    customFieldSections,
    customFieldSelectedIds,
    onCustomFieldSelectionChange,
  } = props;
  const authorization = useTeammateAuthorization();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const allSections = useMemo(
    () => [
      ...metricColumnSections,
      ...sectionColumnSections,
      ...customFieldSections,
    ],
    [metricColumnSections, sectionColumnSections, customFieldSections],
  );

  const allSelectedIds = useMemo(
    () => [
      ...metricColumnSelectedIds,
      ...sectionColumnSelectedIds,
      ...customFieldSelectedIds,
    ],
    [metricColumnSelectedIds, sectionColumnSelectedIds, customFieldSelectedIds],
  );

  // Build sets of all IDs belonging to each selector
  const metricOptionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const section of metricColumnSections) {
      for (const option of section.options) {
        ids.add(option.id);
      }
    }
    return ids;
  }, [metricColumnSections]);

  const sectionOptionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const section of sectionColumnSections) {
      for (const option of section.options) {
        ids.add(option.id);
      }
    }
    return ids;
  }, [sectionColumnSections]);

  const customFieldOptionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const section of customFieldSections ?? []) {
      for (const option of section.options) {
        ids.add(option.id);
      }
    }
    return ids;
  }, [customFieldSections]);

  const defaultsPatch = buildPlaybookViewColumnDefaultsPatch(
    {
      ...(onMetricColumnSelectionChange
        ? { metric_column_ids: [...metricColumnDefaultSelectedIds] }
        : {}),
      ...(onSectionColumnSelectionChange
        ? {
            section_column_ids: sectionColumnSelectedIds.filter(
              (id) => id !== "__playbook__",
            ),
            playbook_column_visible:
              sectionColumnSelectedIds.includes("__playbook__"),
          }
        : {}),
      ...(customFieldSections.length > 0
        ? { custom_field_column_ids: [...customFieldSelectedIds] }
        : {}),
    },
    viewDefaults,
    {
      metric_column_ids: metricOptionIds,
      section_column_ids: sectionOptionIds,
      custom_field_column_ids: customFieldOptionIds,
    },
  );
  const canSetDefault =
    playbookId !== undefined &&
    view !== undefined &&
    defaultsPatch !== undefined &&
    authorization.can(
      PermissionActions.UPDATE,
      PermissionSubjects.RECORDING_ASSESSMENT_TEMPLATES,
    );

  const handleChange = useCallback(
    (keys: readonly string[]) => {
      const metricKeys = keys.filter((k) => metricOptionIds.has(k));
      const sectionKeys = keys.filter((k) => sectionOptionIds.has(k));
      const customFieldKeys = keys.filter((k) => customFieldOptionIds.has(k));
      if (!checkSameSelection(metricKeys, metricColumnSelectedIds)) {
        onMetricColumnSelectionChange?.(metricKeys);
      }
      if (!checkSameSelection(sectionKeys, sectionColumnSelectedIds)) {
        onSectionColumnSelectionChange?.(sectionKeys);
      }
      if (!checkSameSelection(customFieldKeys, customFieldSelectedIds)) {
        onCustomFieldSelectionChange(customFieldKeys);
      }
    },
    [
      metricColumnSelectedIds,
      sectionColumnSelectedIds,
      customFieldSelectedIds,
      metricOptionIds,
      sectionOptionIds,
      customFieldOptionIds,
      onMetricColumnSelectionChange,
      onSectionColumnSelectionChange,
      onCustomFieldSelectionChange,
    ],
  );

  if (
    allSections.length === 0 ||
    allSections.every((s) => s.options.length === 0)
  ) {
    return null;
  }

  return (
    <>
      {canSetDefault && playbookId && view && defaultsPatch ? (
        <SetPlaybookViewDefaultConfirmation
          playbookId={playbookId}
          view={view}
          kind="columns"
          defaults={defaultsPatch}
          isOpen={isConfirmationOpen}
          onOpenChange={setIsConfirmationOpen}
        />
      ) : null}
      <MenuTrigger isOpen={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DisplayColumnsMenuTrigger />
        <MultiSelectMenuPopover
          footer={
            canSetDefault ? (
              <SimpleMenuFooter>
                <SetPlaybookViewDefaultAction
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsConfirmationOpen(true);
                  }}
                />
              </SimpleMenuFooter>
            ) : undefined
          }
          ariaLabel="Display columns"
          placement="bottom end"
          sections={allSections}
          value={allSelectedIds}
          onChange={handleChange}
        />
      </MenuTrigger>
    </>
  );
};

function checkSameSelection(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return left.length === right.length && left.every((id) => right.includes(id));
}
